import {FlexItem, FlexRow, NestedCard, StyledIcon, SubHeading, Text} from '@forecast-it/design-system';
import React, {useMemo} from 'react';
import {useIntl} from 'react-intl';
import {BUDGET_TYPE} from '../../constants';
import {MODAL_TYPE, showModal} from '../../forecast-app/shared/components/modals/generic_modal_conductor';
import {BaselineEstimateTables} from './BaselineEstimateTables';
import {getTotals} from './Util';
import {
	BaselineItem,
	Company,
	ContextMenuOption,
	EyeOptionMap,
	GroupedBaselineTotals,
	Phase,
	Project,
	ProjectPhaseDeliverable,
} from './BaselineTypes';
import {DeliverableCard} from './DeliverablesCard';
import {FinancialsSummary} from './FinancialsSummary';
import {SelectDeliverableCard} from './SelectDeliverableCard';
import {sortPhasesBySortOrder} from './Util';

const DeliverableIcon = () => <StyledIcon icon="deliverable" size={'l'}></StyledIcon>;

export const PhaseCard = ({
	phase,
	company,
	currency,
	eyeOptionMap,
	editMode,
	setProjectBaselineItems,
	phaseBaselineItems,
	groupedProjectTotals,
	project,
	setProjectPhaseDeliverables,
	projectPhaseDeliverables,
	useDeliverables,
}: {
	phase: Phase;
	company: Company;
	currency: string;
	eyeOptionMap?: EyeOptionMap;
	editMode?: boolean;
	setProjectBaselineItems?: React.Dispatch<React.SetStateAction<BaselineItem[]>>;
	setProjectPhaseDeliverables?: React.Dispatch<React.SetStateAction<ProjectPhaseDeliverable[]>>;
	phaseBaselineItems: BaselineItem[];
	projectPhaseDeliverables: ProjectPhaseDeliverable[];
	groupedProjectTotals?: GroupedBaselineTotals;
	project: Project;
	useDeliverables: boolean;
}): React.JSX.Element => {
	const {formatMessage} = useIntl();
	const {startDay, startYear, startMonth, deadlineDay, deadlineMonth, deadlineYear, name} = phase;

	const projectBudget = project?.baselineTarget || 0;
	const deliverables = project?.deliverables?.edges || [];
	const isFixedPrice = project?.budgetType === BUDGET_TYPE.FIXED_PRICE || project?.budgetType === BUDGET_TYPE.FIXED_PRICE_V2;

	const phaseBaselineTotals = useMemo(() => {
		return getTotals(phaseBaselineItems || []);
	}, [phaseBaselineItems]);

	const projectDeliverablesIdSet = useMemo(
		() => new Set(deliverables.map(deliverable => deliverable?.node?.id).filter(Boolean)),
		[deliverables]
	);

	const deliverablesInPhase = useMemo(
		() =>
			projectPhaseDeliverables?.filter(
				phaseDeliverable =>
					phaseDeliverable.phaseId === phase.id && projectDeliverablesIdSet.has(phaseDeliverable.deliverable?.id)
			) || [],
		[projectPhaseDeliverables, projectDeliverablesIdSet]
	);

	const phaseBaselineItemsNoDeliverable = useMemo(
		() => phaseBaselineItems?.filter(phaseBaselineItem => !phaseBaselineItem.deliverableId) || [],
		[phaseBaselineItems]
	);

	const numberOfDeliverables = deliverablesInPhase.length;

	const showManagePhasesModal = () => {
		const phaseEdges = project?.phases?.edges || [];
		const sortedPhases = sortPhasesBySortOrder(phaseEdges);
		showModal({
			type: MODAL_TYPE.PHASE_MANAGEMENT_MODAL,
			initialPhases: sortedPhases,
			project,
		});
	};

	const contextMenuOptions: ContextMenuOption[] = editMode
		? []
		: [{name: formatMessage({id: 'deliverables.guide_section.phases.button'}), onClick: showManagePhasesModal}];

	return (
		<NestedCard colorTheme={'dark'} dataCy={'phase-nested-card'}>
			<NestedCard.Header
				mainIcon="phase"
				sectionName={formatMessage({id: 'common.phase'})}
				name={name || ''}
				contextMenuOptions={contextMenuOptions}
				showContextMenu={!editMode}
			>
				{numberOfDeliverables > 0 && (
					<FlexItem>
						<FlexRow justifyContent="flex-end">
							<DeliverableIcon />
							<SubHeading>{numberOfDeliverables}</SubHeading>
						</FlexRow>
					</FlexItem>
				)}
				<FlexItem>
					<FlexRow gap="s">
						<Text>{formatMessage({id: 'common.start'})}</Text>
						<SubHeading>{`${startDay}/${startMonth}/${startYear}`}</SubHeading>
					</FlexRow>
				</FlexItem>
				<FlexItem>
					<FlexRow gap="s">
						<Text>{formatMessage({id: 'common.end'})}</Text>
						<SubHeading>{`${deadlineDay}/${deadlineMonth}/${deadlineYear}`}</SubHeading>
					</FlexRow>
				</FlexItem>
			</NestedCard.Header>
			{!useDeliverables && (
				<NestedCard.SummaryContent>
					<FinancialsSummary currency={currency} phaseBaselineTotals={phaseBaselineTotals} />
				</NestedCard.SummaryContent>
			)}
			{useDeliverables ? (
				numberOfDeliverables > 0 ? (
					<NestedCard.CollapsibleContentWrapper>
						{[
							...deliverablesInPhase
								.map(phaseDeliverable => {
									const items = phaseBaselineItems?.filter(
										item =>
											phaseDeliverable?.deliverable?.id &&
											item.deliverableId &&
											item.deliverableId.toString() === phaseDeliverable?.deliverable?.id
									);
									return (
										<NestedCard.Content>
											<DeliverableCard
												phaseDeliverable={phaseDeliverable}
												currency={currency}
												eyeOptionMap={eyeOptionMap}
												editMode={editMode}
												company={company}
												phase={phase}
												setProjectBaselineItems={setProjectBaselineItems}
												baselineItems={items}
												groupedProjectTotals={groupedProjectTotals}
												projectBudget={projectBudget}
												isFixedPrice={isFixedPrice}
												projectDeliverables={deliverables}
												setPhaseDeliverables={setProjectPhaseDeliverables}
											/>
										</NestedCard.Content>
									);
								})
								.filter(Boolean),
							...(phaseBaselineItemsNoDeliverable.length > 0
								? [
										<NestedCard.Content>
											<DeliverableCard
												currency={currency}
												eyeOptionMap={eyeOptionMap}
												editMode={editMode}
												company={company}
												phase={phase}
												setProjectBaselineItems={setProjectBaselineItems}
												baselineItems={phaseBaselineItemsNoDeliverable}
												groupedProjectTotals={groupedProjectTotals}
												projectBudget={projectBudget}
												isFixedPrice={isFixedPrice}
												projectDeliverables={deliverables}
												setPhaseDeliverables={setProjectPhaseDeliverables}
											/>
										</NestedCard.Content>,
								  ]
								: []),
							...(editMode
								? [
										<NestedCard.Content>
											<SelectDeliverableCard
												deliverables={deliverables || []}
												phaseId={phase.id}
												setProjectPhaseDeliverables={setProjectPhaseDeliverables}
												deliverablesInPhase={deliverablesInPhase}
												project={project}
												text={formatMessage({id: 'deliverable.select_another_deliverable'})}
											/>
										</NestedCard.Content>,
								  ]
								: []),
						]}
					</NestedCard.CollapsibleContentWrapper>
				) : (
					editMode && (
						<NestedCard.UncollapsibleContentWrapper>
							<NestedCard.Content>
								<SelectDeliverableCard
									deliverables={deliverables || []}
									phaseId={phase.id}
									setProjectPhaseDeliverables={setProjectPhaseDeliverables}
									deliverablesInPhase={deliverablesInPhase}
									project={project}
									text={formatMessage({id: 'deliverable.select_deliverable'})}
								/>
							</NestedCard.Content>
						</NestedCard.UncollapsibleContentWrapper>
					)
				)
			) : (
				<NestedCard.CollapsibleContentWrapper>
					<NestedCard.Content>
						<BaselineEstimateTables
							editMode={editMode}
							company={company}
							setProjectBaselineItems={setProjectBaselineItems}
							projectBudget={projectBudget}
							isFixedPrice={isFixedPrice}
							groupedProjectTotals={groupedProjectTotals}
							phase={phase}
							baselineItems={phaseBaselineItems || []}
							eyeOptionMap={eyeOptionMap}
						/>
					</NestedCard.Content>
				</NestedCard.CollapsibleContentWrapper>
			)}
		</NestedCard>
	);
};
